import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import type { GetStaticProps } from "next";
import Head from "next/head";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import { Footer } from "components/modules/Layout/Footer/Footer";
import { Bold } from "components/common/Bold/Bold";
import { BetaBanner } from "components/common/BetaBanner/BetaBanner";
import Link from "next/link";
import { commonLanguageFiles } from "client/config";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { translateFunc } from "client/utils/translateFunc";
import { Translate } from "components/common/Translate/Translate";
import React, { ReactNode } from "react";
import { capitalize } from "client/utils/capitalize";
import { TrustPilotSection } from "components/modules/TrustPilot/TrustPilotSection";

const TermsPage = () => {
    const terms: {
        number?: string;
        title?: string;
        text?: ReactNode;
        isBold?: boolean;
        subtitle?: ReactNode;
        subtext?: ReactNode;
        email?: ReactNode;
        address1?: ReactNode;
        address2?: ReactNode;
        name?: ReactNode;
        email1?: ReactNode;
        copytext?: ReactNode;
        company?: ReactNode;
        numberedSubtext?: boolean;
    }[] = [
        {
            number: "1.",
            title: translateFunc("entire-agreement"),
            text: <Translate keyName="entire-agreement-text" />,
        },
        {
            number: "2.",
            title: translateFunc(`prohibited-uses`),
            text: <Translate keyName="prohibited-uses-text" />,
        },
        {
            number: "3.",
            title: translateFunc("external-links"),
            text: <Translate keyName="external-links-text" />,
        },
        {
            number: "4.",
            title: translateFunc("termination-access"),
            text: <Translate keyName="termination-access-text" />,
        },
        {
            number: "5.",
            title: translateFunc("disclaimer"),
            text: <Translate keyName="disclaimer-text" />,
        },
        {
            number: "6.",
            title: translateFunc(`limitation-of-liability`),
            text: <Translate keyName="limitation-of-liability-text" />,
        },
        {
            number: "7.",
            title: translateFunc("governing-law"),
            text: <Translate keyName="governing-law-text" />,
        },
        {
            number: "8.",
            title: translateFunc("file-ownership-and-privacy"),
            text: <Translate keyName="file-ownership-and-privacy-text" />,
        },
        {
            number: "9.",
            title: `${translateFunc("copyright-compliance")}`,
            text: <Translate keyName="copyright-compliance-text" />,
        },
        {
            number: "10.",
            title: `${translateFunc("prohibition-title")}`,
        },
        {
            number: "10.1",
            subtitle: `${translateFunc("user-responsibilities")}`,
            text: <Translate keyName="user-responsibilities-text" />,
        },
        {
            number: "10.2",
            subtitle: `${translateFunc("prohibition-of-circumvention-tools")}`,
            text: <Translate keyName="prohibition-of-circumvention-tools-text" />,
        },
        {
            number: "10.3",
            subtitle: `${translateFunc("consequences-of-violation")}`,
            text: <Translate keyName="consequences-of-violation-text" />,
        },
        {
            number: "10.4",
            subtitle: `${translateFunc("user-verification-accountability")}`,
            text: <Translate keyName="user-verification-accountability-text" />,
        },
        {
            number: "10.5",
            subtitle: `${translateFunc("monitoring-and-compliance")}`,
            text: <Translate keyName="monitoring-and-compliance-text" />,
        },
        {
            number: "11.",
            title: `${translateFunc("dmca-compliance")}`,
            text: <Translate keyName="dmca-compliance-text" />,
        },
        {
            number: "11.1",
            subtitle: `${translateFunc("submission-of-dmca-notices")}`,
            text: <Translate keyName="submission-text" />,
            subtext: [
                <Translate keyName="signature" />,
                <Translate keyName="copyrighted-work" />,
                <Translate keyName="infringing-material" />,
                <Translate keyName="contact-info" />,
                <Translate keyName="good-faith-statement" />,
                <Translate keyName="declaration" />,
            ],
            numberedSubtext: true,
        },
        {
            number: "11.2",
            subtitle: `${translateFunc("counter-notification")}`,
            text: <Translate keyName="counter-notification-text" />,
            subtext: [
                <Translate keyName="signature-2" />,
                <Translate keyName="removed-material" />,
                <Translate keyName="good-faith-statement-2" />,
                <Translate keyName="contact-info-2" />,
                <Translate keyName="jurisdiction-consent" />,
            ],
            numberedSubtext: true,
        },
        {
            number: "11.3",
            subtitle: `${translateFunc("repeat-infringers")}`,
            text: <Translate keyName="repeat-infringers-text" />,
        },
        {
            number: "11.4",
            subtitle: `${translateFunc("copyright-agent")}`,

            copytext: <Translate keyName="copyright-agent-text" />,
            email: <Translate keyName="email" />,
            email1: <Translate keyName="email-1" />,
            name: `${translateFunc("name")}`,
            company: `${translateFunc("company")}`,
            address1: `${translateFunc("address1")}`,
            address2: `${translateFunc("address2")}`,
        },
        {
            number: "11.5",
            subtitle: `${translateFunc("policy-updates")}`,
            text: <Translate keyName="policy-updates-text" />,
        },
        {
            number: "12.",
            title: `${translateFunc("subscription-and-billing")}`,
            text: <Translate keyName="subscription-and-billing-text" />,
        },
        {
            number: "13.",
            title: `${translateFunc("refunds")}`,
            text: <Translate keyName="refunds-text" />,
        },
        {
            number: "14.",
            title: `${translateFunc("account-cancellation")}`,
            text: <Translate keyName="account-cancellation-text" />,
        },
        {
            number: "15.",
            title: `${translateFunc("copyright")}`,
            text: <Translate keyName="copyright-text" />,
        },
        {
            number: "16.",
            title: `${translateFunc("affiliate-disclosure")}`,
            text: <Translate keyName="affiliate-disclosure-text" />,
        },
        {
            number: "17.",
            title: `${translateFunc("contact")}`,
            text: (
                <>
                    <Translate keyName="contact-text" /> <Translate keyName="helpdesk-email" />
                </>
            ),
        },
    ];

    return (
        <>
            <Head>
                <title>{`${translateFunc("terms-of-service")} | ImageResizer.com`}</title>
            </Head>
            <div className="max-w-1440 page-shadow text-black dark:text-white dark:bg-darkSurface-100">
                <Navbar url="/terms-of-service" />
                <div className="py-8 mx-8 md:mx-24 lg:mx-44 flex flex-col gap-8 leading-relaxed">
                    <BetaBanner />
                    <h1 className="text-3xl font-semibold">
                        <Translate keyName="terms-of-service" />
                    </h1>
                    <div>
                        <Translate
                            keyName="sub-title"
                            linkRenderer={(_, key) => (
                                <Link href="/privacy" key={key} className="text-irBlue-600 capitalize">
                                    <Translate keyName="privacy-policy" />
                                </Link>
                            )}
                        />
                    </div>
                    {terms.map((term, index) => (
                        <div key={index} className="flex flex-col gap-4">
                            {term.title && (
                                <div className="text-2xl text-[#555555] dark:text-white font-bold flex">
                                    {term.number && <span className="mr-2">{term.number}</span>}
                                    {term.title}
                                </div>
                            )}
                            {term.subtitle && (
                                <div className="ml-8 text-xl font-bold text-[#555555] dark:text-white flex">
                                    {term.number && <span className="mr-2">{term.number}</span>}
                                    {term.subtitle}
                                </div>
                            )}
                            {term.text && (
                                <div
                                    className={`${term.isBold && "font-semibold"} ${term.title ? "ml-8" : term.subtitle ? "ml-20" : ""}`}
                                >
                                    {term.text}
                                </div>
                            )}

                            {term.subtext && term.numberedSubtext && (
                                <div className={`${term.subtitle ? "ml-20" : "ml-8"}`}>
                                    {(Array.isArray(term.subtext) ? term.subtext : [term.subtext]).map((text, i) => (
                                        <div key={i} className="flex mb-2">
                                            <span className="mr-2 font-medium">{`${term.number}.${i + 1}`}</span>
                                            <div>{text}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {term.subtext && !term.numberedSubtext && (
                                <ol className={`list-decimal space-y-2 ${term.subtitle ? "ml-20" : "ml-8"}`}>
                                    {(Array.isArray(term.subtext) ? term.subtext : [term.subtext]).map((text, i) => (
                                        <li key={i}>{text}</li>
                                    ))}
                                </ol>
                            )}

                            {term.name && (
                                <div className={`${term.subtitle ? "ml-20" : "ml-8"}`}>
                                    <div>{term.copytext}</div>
                                    <div>
                                        <span className="font-bold">{term.email}</span> {term.email1}
                                    </div>

                                    <span className="font-bold">{translateFunc("physical-address")}:</span>

                                    <div className="mt-1">{term.name}</div>
                                    <div>{term.company}</div>
                                    <div>{term.address1}</div>
                                    <div>{term.address2}</div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <TrustPilotSection />
                <Footer url="/terms-of-service" />
            </div>
        </>
    );
};
const files = ["terms", ...commonLanguageFiles];

const LanguageWrapper = () => (
    <LanguageProvider files={files}>
        <TermsPage />
    </LanguageProvider>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
